<template>
  <section>
    <component
      :is="currentTabComponent"
      :user-id="currentUserId"
      :read-only="readOnly"
      :use-data-source="true"
      :user-data="getUserData"
      @saveUserData="saveUserData"
      @deleteUserData="handleDeleteUser"
      ref="componentRef"
    />
  </section>
</template>

<script lang="ts" setup>
  import { computed, shallowRef, toRefs } from 'vue';
  import UserTab from './UserTab.vue';
  import { useUserStore } from '../user-store';
  import { UserData } from '../user-data.interface';
  import AccessGuard from '@/app/shared/guards/access-guard';

  interface UserDetailEvents {
    (_event: 'userCreated'): void;
    (_event: 'closeCurrentTab', _params: number): void;
    (_event: 'deleteById', _params: number | string): void;
  }

  interface UserDetailProps {
    currentUserId: number;
  }
  const currentTabComponent = shallowRef(UserTab);
  const props = defineProps<UserDetailProps>();
  const emit = defineEmits<UserDetailEvents>();
  const userStore = useUserStore();
  const { currentUserId } = toRefs(props);

  const getUserData = computed(() => {
    const userDetail = userStore.getUserById(currentUserId.value);
    const userData = userDetail?.userData;
    return {
      id: userData?.id || -1,
      login: userData?.login || '',
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      emailAddress: userData?.emailAddress || '',
      notes: userData?.notes || '',
      password: userData?.password || '',
      superUser: userData?.superUser || false,
      canChangePassword: userData?.canChangePassword || false,
      forcePasswordChange: userData?.forcePasswordChange || false,
      roles: userData?.roles || [],
    };
  });

  const readOnly = computed(() => !AccessGuard.canModify('USER'));

  const saveUserData = (userData: UserData) => {
    if (userData.id == -1) {
      saveData(userData);
    } else {
      updateData(userData);
    }
  };

  const saveData = async (userData: UserData) => {
    try {
      await userStore.createUser(userData);
      emit('closeCurrentTab', currentUserId.value);
    } catch (error) {
      console.error(error);
    }
  };

  const updateData = async (userData: UserData) => {
    try {
      await userStore.updateUser(currentUserId.value, userData);
      emit('closeCurrentTab', currentUserId.value);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteUser = (userId: number | string) => {
    emit('deleteById', userId);
  };
</script>
